import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonIcon from '@mui/icons-material/Person';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import TimerIcon from '@mui/icons-material/Timer';
import TranslateIcon from '@mui/icons-material/Translate';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import React from 'react';

import { Box, Checkbox, IconButton, Chip as MuiChip, Paper, Tooltip, Typography, useTheme } from '@mui/material';
import Chip from '@mui/material/Chip';
import { alpha } from '@mui/material/styles';
import { useState } from 'react';
import { useAssignmentContext } from '../../contexts/AssignmentContext';
import { useAssignments } from '../../hooks/useAssignments';
import { isAdmin } from '../../utils/authUtils';
import { FileTypes } from '../../utils/constants';
import { formatDate, formatDuration, formatUserName, formatVideoName } from '../../utils/formatUtils';
import {
    getBookLanguageChipColor,
    getBookLanguageChipLabel,
    getBookLanguageStageLabel,
    getChipColor,
    getChipLabel,
    getEffectiveTextProcessStage,
    getProcessStageLabel,
    getTextChipColor,
    getTextChipLabel,
    isSourceLanguage,
} from '../../utils/processStageUtils';
import LanguagePopover from '../common/LanguagePopover';
import UpdateStatusDialog from '../dialogs/UpdateStatusDialog';
import { AudioFailedChip, VideoFailedChip } from '../tasks/TaskIndicators';
import useTaskSummary from '../tasks/useTaskSummary';
import { NAV_ICON_TEXT_GAP } from './navigationSpacing';
/**
 * Grid item component for displaying files in grid view
 *
 * @param {Object} props Component props
 * @param {Object} props.file The file object to display
 * @param {Function} props.onFileClick Handler for when the file is clicked
 * @param {Function} props.onMenuOpen Handler for when the context menu is opened
 */
const FileGridItem = ({ file, onVideoFileClick, onTextFileClick, onAudioFileClick, onBookFileClick, onMenuOpen, showPath, type, isSelectMode = false, isSelected = false, onSelectToggle }) => {
    const theme = useTheme();
    const [languagePopover, setLanguagePopover] = React.useState({
        open: false,
        anchorEl: null,
    });

    // Use the custom hook to get assignments
    const { assignments, loading: assignmentsLoading, refresh: refreshAssignments } = useAssignments(file.id);

    // Add state for status update dialog
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [selectedAssignment, setSelectedAssignment] = useState(null);

    // Use the assignment context for refreshing
    const { fetchMyAssignments, fetchAssignedByMe } = useAssignmentContext();

    const handleClick = (event) => {
        // Don't navigate if clicking on a chip or button
        if (event.target.closest('.MuiChip-root') || event.target.closest('button')) {
            return;
        }

        if (isSelectMode) {
            const fileKey = `${type}-${file.data}`;
            if (onSelectToggle) onSelectToggle(fileKey, { ...file, type });
            return;
        }

        if (type === FileTypes.VIDEO) {
            if (onVideoFileClick) {
                onVideoFileClick(file);
            }
        } else if (type === FileTypes.TEXT) {
            if (onTextFileClick) {
                onTextFileClick(file);
            }
        } else if (type === FileTypes.AUDIO) {
            if (onAudioFileClick) {
                onAudioFileClick(file);
            }
        } else if (type === FileTypes.BOOK) {
            if (onBookFileClick) {
                onBookFileClick(file);
            }
        }
    };

    const handleMenuClick = (event) => {
        event.stopPropagation();
        if (onMenuOpen) {
            onMenuOpen(event, file);
        }
    };

    const handleLanguageChipClick = (event) => {
        // Stop propagation to prevent opening the file
        event.stopPropagation();
        event.preventDefault();

        // Open popover
        setLanguagePopover({
            open: true,
            anchorEl: event.currentTarget,
        });
    };

    const handleLanguageCountClick = (event) => {
        // Stop propagation to prevent opening the file
        event.stopPropagation();
        event.preventDefault();

        // Set the anchor element for the popover
        setLanguagePopover({
            open: true,
            anchorEl: event.currentTarget,
        });
    };

    const handleLanguagePopoverClose = () => {
        setLanguagePopover({
            open: false,
            anchorEl: null,
        });
    };

    const handleAssignmentChipClick = (event, assignment) => {
        // Always stop propagation immediately
        event.stopPropagation();
        event.preventDefault();

        // Only proceed if this is the user's assignment or user is admin/teamlead
        const authUser = JSON.parse(localStorage.getItem('authUser') || '{}');
        if (
            assignment.assignedTo === authUser.email ||
            authUser.userType === 'admin' ||
            authUser.userType === 'teamlead'
        ) {
            setSelectedAssignment(assignment);
            setStatusDialogOpen(true);
        }
    };

    // Get last updated by (if available)
    const updatedBy = file.lastModifiedBy || file.updatedBy || 'Unknown';
    const updatedAt = file.lastModifiedDate || file.updatedAt || null;

    // Get file path for display in search results
    const displayPath = showPath && file.path ? file.path : null;

    // Get languages from file with their status
    const languagesWithStatus =
        file.languages?.map((lang) => {
            // If languages is already an object with status, use it directly
            if (typeof lang === 'object' && lang.name && lang.status) {
                return {
                    ...lang,
                    processStage: lang.processStage || 'UNKNOWN',
                };
            }

            // If we have videoLanguages directly, use that
            const matchingLang = file.videoLanguages?.find((vl) => vl.languageName === lang);
            if (matchingLang) {
                return {
                    name: matchingLang.languageName,
                    status: matchingLang.status,
                    processStage: matchingLang.processStage || 'UNKNOWN',
                };
            }

            // Default case
            return {
                name: lang,
                status: 'UNKNOWN',
                processStage: 'UNKNOWN',
            };
        }) || [];

    const hasAssignments = assignments && assignments.length > 0;
    const myEmail = JSON.parse(localStorage.getItem('authUser'))?.email || '';
    const assignedToMe = hasAssignments && assignments.some((a) => a.assignedTo === myEmail);

    // Add this function inside your FileGridItem component, after the languagesWithStatus declaration
    const getFilteredLanguages = () => {
        try {
            // Get user info from localStorage
            const userInfo = JSON.parse(localStorage.getItem('authUser')) || {};
            const userLanguageAccess = userInfo.languageAccess || {};

            // If admin, show all languages
            if (isAdmin()) {
                return languagesWithStatus;
            }

            // For non-admins, filter languages
            return languagesWithStatus.filter((lang) => {
                // Get language name from lang.name
                const langName = lang.name;

                if (!langName) return false;

                // Always show English and Gujarati (case insensitive)
                const langNameLower = langName.toLowerCase();
                if (langNameLower === 'english' || langNameLower === 'gujarati') {
                    return true;
                }

                // Show languages where user has write access
                return userLanguageAccess[langName] && userLanguageAccess[langName].includes('WRITE');
            });
        } catch (error) {
            console.error('Error filtering languages:', error);
            return languagesWithStatus; // Return all languages if there's an error
        }
    };

    // Get filtered languages
    // A language's process stage isn't for AV (6j-6)
    const avOnly = !!useTaskSummary()?.avOnly;
    const filteredLanguages = avOnly ? [] : getFilteredLanguages();

    return (
        <Paper
            elevation={1}
            className="file-folder-item"
            sx={{
                p: 1,
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                transition: 'all 0.2s',
                ...(isSelected && {
                    backgroundColor: (theme) =>
                        theme.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.2)' : 'rgba(211, 47, 47, 0.08)',
                    outline: '2px solid',
                    outlineColor: 'error.main',
                }),
                '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.03)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                },
            }}
            onClick={handleClick}
        >
            {/* Header with file icon and menu */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: NAV_ICON_TEXT_GAP }}>
                {isSelectMode ? (
                    <Checkbox
                        checked={isSelected}
                        size="small"
                        color="error"
                        onClick={(e) => {
                            e.stopPropagation();
                            const fileKey = `${type}-${file.data}`;
                            if (onSelectToggle) onSelectToggle(fileKey, { ...file, type });
                        }}
                        sx={{ p: 0.25 }}
                    />
                ) : (
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid',
                        borderColor:
                            type === FileTypes.VIDEO
                                ? (theme) => alpha(theme.palette.secondary.main, 0.35)
                                : type === FileTypes.TEXT
                                ? (theme) => alpha(theme.palette.info.main, 0.35)
                                : type === FileTypes.BOOK
                                ? (theme) => alpha(theme.palette.success.main, 0.35)
                                : (theme) => alpha(theme.palette.warning.main, 0.35),
                        backgroundColor:
                            type === FileTypes.VIDEO
                                ? (theme) => alpha(theme.palette.secondary.main, 0.12)
                                : type === FileTypes.TEXT
                                ? (theme) => alpha(theme.palette.info.main, 0.12)
                                : type === FileTypes.BOOK
                                ? (theme) => alpha(theme.palette.success.main, 0.12)
                                : (theme) => alpha(theme.palette.warning.main, 0.12),
                    }}
                >
                    {type === FileTypes.VIDEO && <VideoFileIcon sx={{ fontSize: '1.4rem', color: 'secondary.main' }} />}
                    {type === FileTypes.TEXT && <TextSnippetIcon sx={{ fontSize: '1.4rem', color: 'info.main' }} />}
                    {type === FileTypes.AUDIO && <AudiotrackIcon sx={{ fontSize: '1.4rem', color: 'warning.main' }} />}
                    {type === FileTypes.BOOK && <AutoStoriesIcon sx={{ fontSize: '1.4rem', color: 'success.main' }} />}
                </Box>
                )}
                <IconButton
                    size="small"
                    data-testid={`file-menu-${file.videoName || file.name}`}
                    onClick={handleMenuClick}
                    aria-label="more options"
                    className="file-folder-action-menu-button"
                    sx={{
                        opacity: { xs: 1, sm: 0.7 },
                        p: 0.5,
                        '&:hover': {
                            backgroundColor: 'rgba(0,0,0,0.08)',
                        },
                    }}
                >
                    <MoreVertIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* File name section */}
            <Box className="file-folder-name" sx={{ mb: 0.5, mt: 0 }}>
                <Typography variant="subtitle1" noWrap sx={{ fontWeight: 500 }}>
                    {formatVideoName(file.videoName || file.name)}
                </Typography>

                {/* Combined duration and video ID on same line */}
                <Box
                    className="file-folder-duration"
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                    {file.duration && (
                        <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                            <TimerIcon sx={{ fontSize: '0.8rem', mr: 0.5 }} />
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                {formatDuration(file.duration)}
                            </Typography>
                        </Box>
                    )}

                    {file.data && (
                        <Typography variant="caption" color="text.secondary" noWrap>
                            ID: {file.data}
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Path display if needed */}
            {showPath && displayPath && (
                <Typography variant="caption" color="text.secondary" noWrap sx={{ mb: 0.5 }}>
                    <FolderOpenIcon sx={{ fontSize: '0.8rem', mr: 0.5, verticalAlign: 'text-bottom' }} />
                    {displayPath}
                </Typography>
            )}

            {/* Languages section for only videos*/}
            {type === FileTypes.VIDEO && (
                <Box
                    className="folder-file-details"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mt: 0.5,
                        mb: 0.5,
                        cursor: filteredLanguages.length > 0 ? 'pointer' : 'default',
                    }}
                    onClick={filteredLanguages.length > 0 ? handleLanguageChipClick : undefined}
                >
                    <TranslateIcon sx={{ fontSize: '0.85rem', mr: 0.5, color: 'text.secondary' }} />
                    <VideoFailedChip file={file} />
                    {filteredLanguages && filteredLanguages.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3 }} className="grid-file-languages">
                            {filteredLanguages.map((lang) => (
                                <Tooltip
                                    key={lang.name}
                                    title={`${lang.name} (${getProcessStageLabel(lang.processStage)})`}
                                    arrow
                                >
                                    <MuiChip
                                        label={getChipLabel(lang.name, lang.processStage)}
                                        size="small"
                                        sx={{
                                            fontSize: '0.65rem',
                                            height: '18px',
                                            backgroundColor: `${getChipColor(lang.processStage, theme.palette.mode)}20`,
                                            color: getChipColor(lang.processStage, theme.palette.mode),
                                            borderColor: getChipColor(lang.processStage, theme.palette.mode),
                                            border: '1px solid',
                                            fontWeight: 500,
                                            borderRadius: '16px',
                                        }}
                                    />
                                </Tooltip>
                            ))}
                        </Box>
                    ) : (
                        <Typography variant="body2" color="text.secondary" noWrap>
                            -
                        </Typography>
                    )}
                </Box>
            )}

            {type === FileTypes.AUDIO && file.dubbingStatus === 'FAILED' && (
                <Box sx={{ mt: 0.5 }}>
                    <AudioFailedChip file={file} />
                </Box>
            )}

            {/* Languages section for text files - new addition */}
            {type === FileTypes.TEXT && file.translations && (
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {Object.keys(file.translations).length > 0 ? (
                        <>
                            {Object.entries(file.translations)
                                .slice(0, 3)
                                .map(([langName, langData]) => (
                                    <Tooltip
                                        key={langName}
                                        title={`${langName} (${getProcessStageLabel(getEffectiveTextProcessStage(langData))})${
                                            isSourceLanguage(langData) ? ' • Source Language' : ''
                                        }`}
                                        arrow
                                    >
                                        <Chip
                                            label={getTextChipLabel(langName, langData)}
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                handleLanguageCountClick(e);
                                            }}
                                            sx={{
                                                fontSize: '0.7rem',
                                                backgroundColor: `${getChipColor(getEffectiveTextProcessStage(langData), theme.palette.mode)}20`,
                                                color: getTextChipColor(getEffectiveTextProcessStage(langData), theme.palette.mode),
                                                borderColor: getTextChipColor(getEffectiveTextProcessStage(langData), theme.palette.mode),
                                                border: '1px solid',
                                                fontWeight: 500,
                                                borderRadius: '16px',
                                                cursor: 'pointer',
                                            }}
                                        />
                                    </Tooltip>
                                ))}
                            {Object.keys(file.translations).length > 3 && (
                                <Tooltip title={`${Object.keys(file.translations).length - 3} more languages`}>
                                    <Chip
                                        label={`+${Object.keys(file.translations).length - 3}`}
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            handleLanguageCountClick(e);
                                        }}
                                        sx={{
                                            fontSize: '0.7rem',
                                            backgroundColor: 'rgba(158, 158, 158, 0.1)',
                                            color: 'text.secondary',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            fontWeight: 500,
                                            cursor: 'pointer',
                                        }}
                                    />
                                </Tooltip>
                            )}
                        </>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            No languages
                        </Typography>
                    )}
                </Box>
            )}

            {/* Languages section for books */}
            {type === FileTypes.BOOK && (
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {file.targetLanguageStatus && Object.keys(file.targetLanguageStatus).length > 0 ? (
                        <>
                            {Object.entries(file.targetLanguageStatus)
                                .slice(0, 3)
                                .map(([langName, langStatus]) => (
                                    <Tooltip
                                        key={langName}
                                        title={`${langName} (${getBookLanguageStageLabel(langStatus?.processStage)})`}
                                        arrow
                                    >
                                        <Chip
                                            label={getBookLanguageChipLabel(langName, langStatus?.processStage)}
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                handleLanguageCountClick(e);
                                            }}
                                            sx={{
                                                fontSize: '0.7rem',
                                                backgroundColor: `${getBookLanguageChipColor(langStatus?.processStage, theme.palette.mode)}20`,
                                                color: getBookLanguageChipColor(langStatus?.processStage, theme.palette.mode),
                                                borderColor: getBookLanguageChipColor(langStatus?.processStage, theme.palette.mode),
                                                border: '1px solid',
                                                fontWeight: 500,
                                                borderRadius: '16px',
                                                cursor: 'pointer',
                                            }}
                                        />
                                    </Tooltip>
                                ))}
                            {Object.keys(file.targetLanguageStatus).length > 3 && (
                                <Tooltip title={`${Object.keys(file.targetLanguageStatus).length - 3} more languages`}>
                                    <Chip
                                        label={`+${Object.keys(file.targetLanguageStatus).length - 3}`}
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            handleLanguageCountClick(e);
                                        }}
                                        sx={{
                                            fontSize: '0.7rem',
                                            backgroundColor: 'rgba(158, 158, 158, 0.1)',
                                            color: 'text.secondary',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            fontWeight: 500,
                                            cursor: 'pointer',
                                        }}
                                    />
                                </Tooltip>
                            )}
                        </>
                    ) : (
                        <Chip
                            label={file.processStage || 'UPLOADED'}
                            size="small"
                            sx={{
                                fontSize: '0.7rem',
                                backgroundColor: (theme) => alpha(theme.palette.success.main, 0.12),
                                color: 'success.main',
                                border: '1px solid',
                                borderColor: (theme) => alpha(theme.palette.success.main, 0.35),
                                fontWeight: 500,
                                borderRadius: '16px',
                            }}
                        />
                    )}
                </Box>
            )}

            {/* Assignments section */}
            {hasAssignments && (
                <Box sx={{ mt: 1, mb: 1 }} className="grid-file-assignments">
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 0.5,
                        }}
                    >
                        <AssignmentIcon sx={{ fontSize: '0.85rem', mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            {assignments.length} assignment{assignments.length !== 1 ? 's' : ''}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }} className="list-file-assignments">
                        {assignments.map((assignment, index) => {
                            if (assignment.assignedTo !== myEmail) {
                                // Skip assignments not assigned to the current user
                                return null;
                            }
                            // Calculate if overdue
                            const isOverdue =
                                new Date(assignment.dueDate) < new Date() && assignment.status !== 'COMPLETED';

                            // Format assignment status for display
                            const statusDisplay =
                                assignment.status === 'COMPLETED'
                                    ? '✓'
                                    : assignment.status === 'IN_PROGRESS'
                                    ? '⟳'
                                    : isOverdue
                                    ? '⚠️'
                                    : '';

                            // Determine chip color based on status and due date
                            let chipColor = 'default';
                            if (assignment.status === 'COMPLETED') {
                                chipColor = 'success';
                            } else if (isOverdue) {
                                chipColor = 'error';
                            } else if (assignment.status === 'IN_PROGRESS') {
                                chipColor = 'primary';
                            } else if (assignment.assignedTo === myEmail) {
                                chipColor = 'warning';
                            }

                            // Create display text with Level and Status
                            const levelDisplay = assignment.category || '';
                            const chipLabel = `${levelDisplay ? levelDisplay + ' - ' : ''}${assignment.languageName}${
                                statusDisplay ? ' ' + statusDisplay : ''
                            }`;

                            // Determine if this chip is clickable
                            const isMyAssignmentOrAdmin = assignment.assignedTo === myEmail;

                            return (
                                <Tooltip
                                    key={index}
                                    title={`${assignment.languageName} (${
                                        assignment.category || 'No Level'
                                    }): Assigned to ${assignment.assignedTo}
                                    Due: ${new Date(assignment.dueDate).toLocaleDateString()}
                                    Status: ${assignment.status}
                                    ${isMyAssignmentOrAdmin ? 'Click to update status' : ''}`}
                                    arrow
                                >
                                    <Chip
                                        label={chipLabel}
                                        size="small"
                                        color={chipColor}
                                        variant={assignment.assignedTo === myEmail ? 'filled' : 'outlined'}
                                        onClick={(e) => {
                                            // Always stop propagation, regardless of who can update
                                            e.stopPropagation();
                                            e.preventDefault();
                                            if (isMyAssignmentOrAdmin) {
                                                handleAssignmentChipClick(e, assignment);
                                            }
                                        }}
                                        sx={{
                                            fontSize: '0.65rem',
                                            height: 'auto',
                                            py: 0.2,
                                            fontWeight: assignment.assignedTo === myEmail ? 600 : 400,
                                            cursor: isMyAssignmentOrAdmin ? 'pointer' : 'default',
                                            '&:hover': isMyAssignmentOrAdmin
                                                ? {
                                                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                                      transform: 'translateY(-1px)',
                                                  }
                                                : {},
                                        }}
                                    />
                                </Tooltip>
                            );
                        })}
                    </Box>
                </Box>
            )}

            {hasAssignments && (
                <Box sx={{ mt: 0.5 }}>
                    {assignments.map((assignment, index) => {
                        // Skip if no due date
                        if (!assignment.dueDate) return null;

                        // Calculate days remaining
                        const dueDate = new Date(assignment.dueDate);
                        const today = new Date();
                        const diffTime = dueDate - today;
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                        // Format the date string
                        const dueDateStr = dueDate.toLocaleDateString();

                        // Determine text color based on days remaining
                        let textColor = 'text.secondary';
                        if (diffDays < 0 && assignment.status !== 'COMPLETED') {
                            textColor = 'error.main'; // Overdue
                        } else if (diffDays <= 2 && assignment.status !== 'COMPLETED') {
                            textColor = 'warning.main'; // Due soon
                        }

                        // Only show the nearest due date if there are multiple
                        if (index === 0 || (index > 0 && dueDate < new Date(assignments[0].dueDate))) {
                            return (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        mt: 0.5,
                                    }}
                                >
                                    <AccessTimeIcon sx={{ fontSize: '0.8rem', mr: 0.5, color: textColor }} />
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: textColor,
                                            fontWeight: diffDays < 0 || diffDays <= 2 ? 600 : 400,
                                            fontSize: '0.7rem',
                                        }}
                                    >
                                        Due: {dueDateStr}
                                        {diffDays < 0 && assignment.status !== 'COMPLETED'
                                            ? ' (overdue)'
                                            : diffDays === 0 && assignment.status !== 'COMPLETED'
                                            ? ' (today)'
                                            : diffDays === 1 && assignment.status !== 'COMPLETED'
                                            ? ' (tomorrow)'
                                            : diffDays > 1 && assignment.status !== 'COMPLETED'
                                            ? ` (${diffDays} days)`
                                            : ''}
                                    </Typography>
                                </Box>
                            );
                        }
                        return null;
                    })}
                </Box>
            )}

            {/* Combined metadata section - both items on one row */}
            <Box sx={{ mt: 'auto', pt: 0.5 }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 1,
                    }}
                >
                    <Box className="file-folder-updated-by" sx={{ display: 'flex', alignItems: 'center' }}>
                        <PersonIcon sx={{ fontSize: '0.85rem', mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.75rem' }}>
                            {formatUserName(updatedBy)}
                        </Typography>
                    </Box>

                    <Box className="file-folder-updated-at" sx={{ display: 'flex', alignItems: 'center' }}>
                        <AccessTimeIcon c sx={{ fontSize: '0.85rem', mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.75rem' }}>
                            {formatDate(updatedAt)}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <LanguagePopover
                open={languagePopover.open}
                anchorEl={languagePopover.anchorEl}
                onClose={handleLanguagePopoverClose}
                languages={
                    type === FileTypes.TEXT
                        ? file.translations || {}
                        : type === FileTypes.BOOK
                        ? file.targetLanguageStatus || {}
                        : filteredLanguages
                }
                videoLanguages={file.videoLanguages}
                isTextFile={type === FileTypes.TEXT}
                isBookFile={type === FileTypes.BOOK}
            />

            {/* Add status dialog */}
            <UpdateStatusDialog
                open={statusDialogOpen}
                onClose={() => setStatusDialogOpen(false)}
                assignment={selectedAssignment}
                onUpdate={() => {
                    refreshAssignments(); // Refresh local assignments
                    // Refresh assignments after update
                    if (file && file.data) {
                        // Refresh assignments for this video
                        // Trigger refresh in the larger context
                        fetchMyAssignments();
                        fetchAssignedByMe();
                    }
                }}
            />
        </Paper>
    );
};

export default FileGridItem;
